import React, { useState, useEffect, lazy, Suspense } from 'react';
import { SecondaryButton } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import CarAttributesMainGroup from './CarAttributesMainGroup';

const CarAttributesTabs = lazy(() => import('./CarAttributesTabs'));
const CarAttributesModal = lazy(() => import('./CarAttributesModal'));

import Classes from './CarAttributes.scss';

import { TListingCarAttributes, TListingCarDetailGroupedWithIcon, TListingClient } from 'types/TListing';

type CarAttributesGroupedWithIconsReduced = {
  mainGroup: TListingCarDetailGroupedWithIcon | null;
  nonMainGroups: TListingCarDetailGroupedWithIcon[];
};

type TCarAttributesProps = {
  carAttributes?: TListingCarAttributes;
  carDetails: TListingClient['carDetails'];
};

const ONE_COLUMN_ITEMS = 3;
const MAIN_GROUP_KEY = 'Main';
const MIN_WIDTH_FOR_TABS = 768;

const CarAttributes = ({ carAttributes, carDetails }: TCarAttributesProps) => {
  const { t } = useI18nContext();
  const [shouldLoadTabs, setShouldLoadTabs] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkWindowWidth = () => {
      setShouldLoadTabs(window.innerWidth >= MIN_WIDTH_FOR_TABS);
    };

    // Check initial width
    checkWindowWidth();

    window.addEventListener('resize', checkWindowWidth);

    // Cleanup listener on unmount
    return () => window.removeEventListener('resize', checkWindowWidth);
  }, []);

  if (!carAttributes) {
    return null;
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const carAttributesGroupedWithIcons = carAttributes.groupedWithIcons || [];
  const { mainGroup, nonMainGroups } = carAttributesGroupedWithIcons.reduce<CarAttributesGroupedWithIconsReduced>(
    (acc, group) => {
      if (group.key === MAIN_GROUP_KEY) {
        acc.mainGroup = group;
        return acc;
      }
      acc.nonMainGroups.push(group);
      return acc;
    },
    { mainGroup: null, nonMainGroups: [] },
  );

  const mainGroupAttributes = mainGroup ? mainGroup.attributes : [];
  const hasMoreAttributes = nonMainGroups.length > 0;
  const listOverviewClassNames = [Classes.list]
    .concat([mainGroupAttributes.length <= ONE_COLUMN_ITEMS ? Classes.oneColumn : ''])
    .join(' ');

  return (
    <section className={`block-wrapper-s ${Classes.root}`}>
      <div className={listOverviewClassNames}>
        {mainGroupAttributes.map((item) => (
          <CarAttributesMainGroup item={item} key={item.key} carDetails={carDetails} />
        ))}
      </div>

      {hasMoreAttributes && shouldLoadTabs && (
        <Suspense fallback={null}>
          <CarAttributesTabs attributesGroups={nonMainGroups} />
        </Suspense>
      )}

      {hasMoreAttributes && !shouldLoadTabs && (
        <SecondaryButton className={Classes.attributesModalTrigger} onClick={handleOpenModal}>
          {t('listing.carDetails.dialog.trigger')}
        </SecondaryButton>
      )}
      {hasMoreAttributes && isModalOpen && (
        <Suspense fallback={null}>
          <CarAttributesModal handleClose={handleCloseModal} attributesGroups={nonMainGroups} />
        </Suspense>
      )}
    </section>
  );
};

export default CarAttributes;
